import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

/**
 * Codeforces-style achievement stamps/badges displayed inline on the report.
 * Each badge looks like a physical stamp or medal with a circular design,
 * ribbon accent, and bold label.
 */

const BADGE_CONFIG = {
  // Map achievement icon names to visual configs
  default: { ring: 'border-amber-400', bg: 'from-amber-50 to-orange-50', text: 'text-amber-600', glow: 'shadow-amber-400/20', ribbon: 'bg-amber-500' },
  Trophy: { ring: 'border-yellow-400', bg: 'from-yellow-50 to-amber-50', text: 'text-yellow-600', glow: 'shadow-yellow-400/20', ribbon: 'bg-yellow-500' },
  Star: { ring: 'border-purple-400', bg: 'from-purple-50 to-violet-50', text: 'text-purple-600', glow: 'shadow-purple-400/20', ribbon: 'bg-purple-500' },
  Flame: { ring: 'border-orange-400', bg: 'from-orange-50 to-red-50', text: 'text-orange-600', glow: 'shadow-orange-400/20', ribbon: 'bg-orange-500' },
  Zap: { ring: 'border-emerald-400', bg: 'from-emerald-50 to-teal-50', text: 'text-emerald-600', glow: 'shadow-emerald-400/20', ribbon: 'bg-emerald-500' },
  Award: { ring: 'border-blue-400', bg: 'from-blue-50 to-indigo-50', text: 'text-blue-600', glow: 'shadow-blue-400/20', ribbon: 'bg-blue-500' },
  Shield: { ring: 'border-rose-400', bg: 'from-rose-50 to-pink-50', text: 'text-rose-600', glow: 'shadow-rose-400/20', ribbon: 'bg-rose-500' },
  Target: { ring: 'border-cyan-400', bg: 'from-cyan-50 to-sky-50', text: 'text-cyan-600', glow: 'shadow-cyan-400/20', ribbon: 'bg-cyan-500' },
  Crown: { ring: 'border-amber-500', bg: 'from-amber-100 to-yellow-50', text: 'text-amber-700', glow: 'shadow-amber-500/30', ribbon: 'bg-amber-600' },
  CheckCircle: { ring: 'border-emerald-400', bg: 'from-emerald-50 to-green-50', text: 'text-emerald-600', glow: 'shadow-emerald-400/20', ribbon: 'bg-emerald-500' },
  Medal: { ring: 'border-indigo-400', bg: 'from-indigo-50 to-violet-50', text: 'text-indigo-600', glow: 'shadow-indigo-400/20', ribbon: 'bg-indigo-500' },
};

const getConfig = (icon) => BADGE_CONFIG[icon] || BADGE_CONFIG.default;

const StampBadge = ({ achievement, index }) => {
  const { title, icon, isUnlocked } = achievement;
  const iconName = icon || 'Trophy';
  const config = getConfig(iconName);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5, rotate: -15 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ delay: index * 0.1, type: 'spring', stiffness: 300, damping: 18 }}
      whileHover={{ scale: 1.12, rotate: 3, y: -4 }}
      className="relative group cursor-pointer"
    >
      {/* Outer stamp container */}
      <div className={`relative w-16 h-16 md:w-[72px] md:h-[72px] rounded-full flex items-center justify-center
        ${isUnlocked
          ? `bg-gradient-to-br ${config.bg} border-[3px] ${config.ring} shadow-lg ${config.glow}`
          : 'bg-slate-50 border-[3px] border-slate-200 shadow-sm'
        } transition-all duration-300`}
      >
        {/* Inner dashed ring for stamp effect */}
        {isUnlocked && (
          <div className={`absolute inset-1.5 rounded-full border border-dashed ${config.ring} opacity-40`} />
        )}

        {/* Icon */}
        <Icon
          name={isUnlocked ? iconName : 'Lock'}
          size={isUnlocked ? 22 : 16}
          className={`relative z-10 ${isUnlocked ? config.text : 'text-slate-300'} transition-all
            ${isUnlocked ? 'drop-shadow-sm' : 'opacity-50'}
          `}
        />

        {/* Locked overlay frost */}
        {!isUnlocked && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[0.5px] rounded-full" />
        )}

        {/* Ribbon accent at bottom */}
        {isUnlocked && (
          <div className={`absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 ${config.ribbon} rounded-full shadow-md`}>
            <div className="w-4 h-0.5 bg-white/60 rounded-full" />
          </div>
        )}
      </div>

      {/* Title label */}
      <div className="mt-2 text-center max-w-[72px]">
        <span className={`text-[7px] md:text-[8px] font-blacktracking-widest leading-tight block
          ${isUnlocked ? 'text-slate-600' : 'text-slate-300'}
        `}>
          {title}
        </span>
      </div>

      {/* Hover tooltip */}
      <div className="absolute -top-10 left-1/2 -translate-x-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-all duration-200 z-50">
        <div className={`px-3 py-1.5 rounded-lg shadow-xl border whitespace-nowrap
          ${isUnlocked
            ? 'bg-white border-slate-100 text-slate-700'
            : 'bg-slate-800 border-slate-700 text-slate-300'
          }`}
        >
          <span className="text-[9px] font-bold">
            {isUnlocked ? `🏅 ${title}` : `🔒 ${title}`}
          </span>
        </div>
        <div className={`w-2 h-2 rotate-45 mx-auto -mt-1
          ${isUnlocked ? 'bg-white border-r border-b border-slate-100' : 'bg-slate-800'}
        `} />
      </div>
    </motion.div>
  );
};


const AchievementStamps = ({ achievements = [] }) => {
  if (!achievements || achievements.length === 0) return null;

  // Show up to 6 badges for visual balance
  const display = achievements.slice(0, 6);

  return (
    <div className="mt-3">
      <div className="flex items-center gap-1.5 mb-3">
        <Icon name="Award" size={13} className="text-amber-500" />
        <span className="text-[9px] font-black text-slate-400tracking-[0.2em]">Achievements</span>
        {achievements.length > 6 && (
          <span className="text-[8px] font-bold text-slate-300 ml-1">+{achievements.length - 6} more</span>
        )}
      </div>
      <div className="flex flex-wrap items-start gap-3">
        {display.map((achievement, idx) => (
          <StampBadge
            key={achievement.id || idx}
            achievement={achievement}
            index={idx}
          />
        ))}
      </div>
    </div>
  );
};

export default AchievementStamps;
